<template>
  <HermesDrawer
    :model-value="modelValue"
    :title="drawerTitle"
    @update:model-value="$emit('update:modelValue', $event)"
    @close="$emit('update:modelValue', false)"
  >
    <template #header>
      <div>
        <h3 class="h-pe__title">{{ drawerTitle }}</h3>
        <div class="h-pe__sub">
          {{ productName || 'Product' }}
          <span v-if="form.slug" class="h-pe__mono">· {{ form.slug }}</span>
        </div>
      </div>
    </template>

    <!-- ===== Body ===== -->
    <div class="h-pe">
      <FormKit
        type="text"
        label="Plan name"
        v-model="form.name"
        validation="required"
        :validation-messages="{ required: 'A plan name is required.' }"
        help="Shown to end users in checkout and the vendor portal."
      />

      <FormKit
        type="text"
        label="Slug"
        v-model="form.slug"
        validation="required|matches:/^[a-z0-9-]*$/"
        :validation-messages="{ matches: 'Lowercase letters, numbers and hyphens only.' }"
        help="Stable machine id — unique within the product."
      />

      <div class="h-pe__grid2">
        <div class="h-pe__field">
          <label class="h-pe__label">Price <span class="h-pe__req">*</span></label>
          <div class="h-pe__prefixwrap">
            <span class="h-pe__prefix">{{ currencySymbol }}</span>
            <input
              class="h-pe__input h-pe__input--suffixed"
              type="text"
              inputmode="decimal"
              v-model="form.price_amount"
              @blur="normalisePrice"
            />
          </div>
          <p v-if="priceError" class="h-pe__err">{{ priceError }}</p>
        </div>

        <FormKit
          type="select"
          label="Billing interval"
          v-model="form.billing_interval"
          :options="intervalOptions"
        />
      </div>

      <FormKit
        type="select"
        label="The price is"
        v-model="form.price_basis"
        :options="priceBasisOptions"
        help="Net: Hermes adds the vendor's VAT at checkout. Gross: the price already includes VAT."
      />

      <div class="h-pe__grid2">
        <FormKit
          type="select"
          label="Currency"
          v-model="form.price_currency"
          :options="currencyOptions"
        />
        <FormKit
          type="select"
          label="Tier"
          v-model="form.tier"
          :options="tierOptions"
          help="Visual grouping only."
        />
      </div>

      <div class="h-pe__grid2">
        <FormKit
          type="number"
          label="Grace period (days)"
          v-model="form.grace_period_days"
          min="0"
          step="1"
        />
        <FormKit
          type="number"
          label="Weight"
          v-model="form.weight"
          step="1"
          help="Sort order (low first)."
        />
      </div>

      <!-- Limits — key/value rows -->
      <div class="h-pe__field">
        <label class="h-pe__label">Limits <span class="h-pe__opt">metric key → value</span></label>
        <div v-for="(row, i) in limitRows" :key="i" class="h-pe__kv">
          <input
            class="h-pe__input h-pe__input--key"
            type="text"
            placeholder="metric.key"
            v-model="row.key"
          />
          <input
            class="h-pe__input h-pe__input--val"
            type="text"
            placeholder="value (blank = unlimited)"
            v-model="row.value"
          />
          <button type="button" class="h-pe__kvx" aria-label="Remove limit" @click="removeLimit(i)">✕</button>
        </div>
        <button type="button" class="h-pe__addrow" @click="addLimit">+ Add limit</button>
        <p class="h-pe__hint">Leave a value blank to mean unlimited (stored as null).</p>
      </div>

      <!-- Features — tag list -->
      <div class="h-pe__field">
        <label class="h-pe__label">Features <span class="h-pe__opt">feature flags</span></label>
        <div class="h-pe__tags">
          <span v-for="(f, i) in form.features" :key="i" class="h-pe__tag">
            {{ f }}
            <button type="button" class="h-pe__tagx" aria-label="Remove feature" @click="removeFeature(i)">✕</button>
          </span>
          <input
            class="h-pe__taginput"
            type="text"
            placeholder="Add feature + Enter"
            v-model="featureDraft"
            @keydown.enter.prevent="addFeature"
            @keydown.,.prevent="addFeature"
          />
        </div>
      </div>

      <!-- Toggles -->
      <label class="h-pe__switch">
        <input type="checkbox" v-model="form.requires_activation" />
        <span class="h-pe__track"></span>
        <span class="h-pe__swlbl">Requires activation (issue signed licence keys)</span>
      </label>
      <label class="h-pe__switch">
        <input type="checkbox" v-model="form.is_active" />
        <span class="h-pe__track"></span>
        <span class="h-pe__swlbl">Plan is active (sellable)</span>
      </label>

      <!-- Gateway sync (existing plans only) -->
      <template v-if="!isNew">
        <div class="h-pe__sec">Gateway sync</div>
        <div v-for="gw in gatewayLines" :key="gw.key" class="h-pe__gwrow">
          <span class="h-pe__gwn"><i :style="{ background: gw.dot }"></i>{{ gw.label }}</span>
          <HermesStatusBadge
            class="h-pe__gwbadge"
            :role="gw.role"
            :label="gw.text"
            status="gw"
          />
        </div>
        <p class="h-pe__hint">
          Saving re-pushes the price to any drifted gateway. Stripe and Revolut objects are never deleted — only superseded.
        </p>
      </template>

      <p v-if="saveError" class="h-pe__saveerr">{{ saveError }}</p>
    </div>

    <!-- ===== Footer ===== -->
    <template #footer>
      <button type="button" class="h-btn h-btn--ghost" @click="$emit('update:modelValue', false)">Cancel</button>
      <button type="button" class="h-btn h-btn--primary" :disabled="saving" @click="save">
        <span v-if="saving" class="h-pe__spin"></span>
        {{ isNew ? 'Create plan' : 'Save & sync' }}
      </button>
    </template>
  </HermesDrawer>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";

// HermesPlanEditorDrawer — create / edit a plan without leaving the catalogue
// (admin-catalogue.html plan-editor drawer). Wraps HermesDrawer; FormKit carries
// the scalar fields, with a custom key/value limits editor + features tag list +
// token-styled toggles. On save it POSTs (new) or PATCHes (existing) the plan,
// then — for an existing plan whose gateway price has drifted — re-pushes via the
// existing sync-stripe-price / sync-revolut-plan endpoints. Emits 'saved'.
export default {
  name: 'HermesPlanEditorDrawer',
  props: {
    modelValue: { type: Boolean, default: false },
    // The plan to edit, or null to create a new one.
    plan: { type: Object, default: null },
    productId: { type: [Number, String], default: null },
    productName: { type: String, default: '' },
    // Default currency for a brand-new plan (from the product's other plans).
    defaultCurrency: { type: String, default: 'GBP' },
    apiBase: { type: String, default: '/api/v1' },
  },
  emits: ['update:modelValue', 'saved'],
  data() {
    return {
      api: useApi(),
      form: this.blankForm(),
      limitRows: [],
      featureDraft: '',
      priceError: '',
      saving: false,
      saveError: '',
      intervalOptions: [
        { label: 'Monthly', value: 'monthly' },
        { label: 'Quarterly', value: 'quarterly' },
        { label: 'Yearly', value: 'yearly' },
        { label: 'One-time', value: 'one_time' },
      ],
      priceBasisOptions: [
        { label: 'Net — VAT is added at checkout', value: 'exclusive' },
        { label: 'Gross — includes VAT', value: 'inclusive' },
      ],
      currencyOptions: [
        { label: 'GBP £', value: 'GBP' },
        { label: 'USD $', value: 'USD' },
        { label: 'EUR €', value: 'EUR' },
      ],
      tierOptions: [
        { label: '—', value: '' },
        { label: 'Starter', value: 'starter' },
        { label: 'Team', value: 'team' },
        { label: 'Pro', value: 'pro' },
        { label: 'Enterprise', value: 'enterprise' },
      ],
    };
  },
  computed: {
    isNew() {
      return !this.plan || !this.plan.id;
    },
    drawerTitle() {
      return this.isNew ? 'New plan' : `Edit plan · ${this.plan?.name || ''}`;
    },
    currencySymbol() {
      return { GBP: '£', USD: '$', EUR: '€' }[this.form.price_currency] || this.form.price_currency;
    },
    gatewayLines() {
      const gw = this.plan?.gateway || {};
      return [
        this.gwLine('stripe', 'Stripe price', '#635BFF', gw.stripe),
        this.gwLine('revolut', 'Revolut variation', '#191C1F', gw.revolut),
      ];
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.seed();
    },
  },
  methods: {
    blankForm() {
      return {
        name: '',
        slug: '',
        tier: '',
        price_amount: '0.00',
        price_currency: this.defaultCurrency || 'GBP',
        price_basis: 'exclusive',
        billing_interval: 'monthly',
        requires_activation: true,
        grace_period_days: 0,
        weight: 0,
        is_active: true,
        features: [],
      };
    },
    seed() {
      this.priceError = '';
      this.saveError = '';
      this.featureDraft = '';
      if (this.isNew) {
        this.form = this.blankForm();
        this.limitRows = [{ key: '', value: '' }];
        return;
      }
      const p = this.plan;
      this.form = {
        name: p.name || '',
        slug: p.slug || '',
        tier: p.tier || '',
        price_amount: p.price_amount != null ? String(p.price_amount) : '0.00',
        price_currency: p.price_currency || 'GBP',
        price_basis: p.price_basis === 'inclusive' ? 'inclusive' : 'exclusive',
        billing_interval: p.billing_interval || 'monthly',
        requires_activation: !!p.requires_activation,
        grace_period_days: Number(p.grace_period_days || 0),
        weight: Number(p.weight || 0),
        is_active: p.is_active !== false,
        features: Array.isArray(p.features) ? [...p.features] : [],
      };
      const limits = p.limits && typeof p.limits === 'object' ? p.limits : {};
      this.limitRows = Object.keys(limits).map((k) => ({
        key: k,
        value: limits[k] === null || limits[k] === undefined ? '' : String(limits[k]),
      }));
      if (!this.limitRows.length) this.limitRows = [{ key: '', value: '' }];
    },
    normalisePrice() {
      const n = parseFloat(this.form.price_amount);
      if (Number.isNaN(n) || n < 0) {
        this.priceError = 'Enter a valid non-negative amount.';
        return;
      }
      this.priceError = '';
      this.form.price_amount = n.toFixed(2);
    },
    addLimit() {
      this.limitRows.push({ key: '', value: '' });
    },
    removeLimit(i) {
      this.limitRows.splice(i, 1);
    },
    addFeature() {
      const v = (this.featureDraft || '').trim().replace(/,$/, '');
      if (v && !this.form.features.includes(v)) this.form.features.push(v);
      this.featureDraft = '';
    },
    removeFeature(i) {
      this.form.features.splice(i, 1);
    },
    buildLimits() {
      const out = {};
      for (const row of this.limitRows) {
        const k = (row.key || '').trim();
        if (!k) continue;
        const raw = (row.value || '').trim();
        if (raw === '') {
          out[k] = null; // unlimited
        } else if (/^-?\d+(\.\d+)?$/.test(raw)) {
          out[k] = Number(raw);
        } else {
          out[k] = raw;
        }
      }
      return out;
    },
    async save() {
      this.normalisePrice();
      if (this.priceError) return;
      if (!this.form.name.trim()) {
        this.saveError = 'A plan name is required.';
        return;
      }
      if (!this.form.slug.trim()) {
        this.saveError = 'A slug is required.';
        return;
      }

      // A change of basis changes what the gateway charges, just as a change of price does.
      const priceChanged = !this.isNew && (String(this.plan.price_amount) !== this.form.price_amount
        || (this.plan.price_basis || 'exclusive') !== this.form.price_basis);

      const payload = {
        name: this.form.name.trim(),
        slug: this.form.slug.trim(),
        tier: this.form.tier || null,
        price_amount: this.form.price_amount,
        price_currency: this.form.price_currency,
        price_basis: this.form.price_basis,
        billing_interval: this.form.billing_interval,
        limits: this.buildLimits(),
        features: this.form.features,
        requires_activation: this.form.requires_activation,
        grace_period_days: Number(this.form.grace_period_days || 0),
        weight: Number(this.form.weight || 0),
        is_active: this.form.is_active,
      };

      this.saving = true;
      this.saveError = '';
      try {
        let saved;
        if (this.isNew) {
          const data = await this.api.post(`${this.apiBase}/admin/products/${this.productId}/plans`, payload);
          saved = data.plan;
        } else {
          const data = await this.api.patch(`${this.apiBase}/admin/plans/${this.plan.id}`, payload);
          saved = data.plan;
          // Re-push a drifted price to each linked gateway (best-effort — a
          // gateway that's unreachable in dev just leaves the drift flagged).
          if (priceChanged) {
            await this.rePushDrift(saved);
          }
        }
        this.$emit('saved', saved);
        this.$emit('update:modelValue', false);
      } catch (e) {
        this.saveError = this.errMsg(e);
      } finally {
        this.saving = false;
      }
    },
    async rePushDrift(saved) {
      const gw = saved?.gateway || {};
      const jobs = [];
      if (gw.stripe?.linked) {
        jobs.push(this.api.post(`${this.apiBase}/admin/plans/${saved.id}/sync-stripe-price`).catch(() => {}));
      }
      if (gw.revolut?.linked) {
        jobs.push(this.api.post(`${this.apiBase}/admin/plans/${saved.id}/sync-revolut-plan?force=1`).catch(() => {}));
      }
      if (jobs.length) await Promise.allSettled(jobs);
    },
    gwLine(key, label, dot, status) {
      const s = status || { linked: false, checked: false };
      let role = 'neutral';
      let text = 'not linked';
      if (!s.linked) {
        role = 'neutral';
        text = 'not linked';
      } else if (!s.checked) {
        role = 'neutral';
        text = s.object_id ? `unverified · ${s.object_id}` : 'unverified';
      } else if (s.drift) {
        role = 'warning';
        text = 'price drift';
      } else {
        role = 'success';
        text = s.object_id ? `synced · ${s.object_id}` : 'synced';
      }
      return { key, label, dot, role, text };
    },
    errMsg(e) {
      const status = e?.response?.status;
      const serverMsg = e?.response?.data?.message;
      if (serverMsg) return serverMsg;
      return `Save failed${status ? ` (${status})` : ''}. ${e?.message || ''}`.trim();
    },
  },
  mounted() {
    if (this.modelValue) this.seed();
  },
};
</script>

<style scoped>
.h-pe { font-family: var(--h-font-body, system-ui); }
.h-pe__title { margin: 0; font-size: 15px; font-weight: 600; color: var(--h-text-primary, #0F172A); }
.h-pe__sub { font-size: 12px; color: var(--h-text-tertiary, #94A3B8); margin-top: 2px; }
.h-pe__mono { font-family: var(--h-font-mono, monospace); }

.h-pe__grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.h-pe__field { margin-bottom: 16px; }
.h-pe__label { display: block; font-size: 13px; font-weight: 500; color: var(--h-text-primary, #0F172A); margin-bottom: 6px; }
.h-pe__req { color: var(--h-danger, #EF4444); margin-left: 2px; }
.h-pe__opt { color: var(--h-text-tertiary, #94A3B8); font-weight: 400; margin-left: 6px; font-size: 12px; }
.h-pe__hint { font-size: 12px; color: var(--h-text-tertiary, #94A3B8); margin: 6px 0 0; }
.h-pe__err, .h-pe__saveerr { font-size: 12.5px; color: var(--h-danger-text, #B91C1C); margin: 6px 0 0; }
.h-pe__saveerr { margin-top: 14px; padding: 10px 12px; background: var(--h-danger-bg, #FEF2F2); border: 1px solid var(--h-danger-border, #FECACA); border-radius: var(--h-radius, 6px); }

.h-pe__input {
  width: 100%; font: inherit; font-size: 14px; background: var(--h-bg-card, #fff);
  border: 1px solid var(--h-border-strong, #CBD5E1); color: var(--h-text-primary, #0F172A);
  border-radius: var(--h-radius, 6px); padding: 9px 12px;
}
.h-pe__input:focus { outline: none; border-color: var(--h-accent, #0EA5E9); box-shadow: var(--h-focus-ring-soft, 0 0 0 3px rgba(14,165,233,.15)); }
.h-pe__prefixwrap { display: flex; align-items: stretch; }
.h-pe__prefix { display: flex; align-items: center; padding: 0 12px; background: var(--h-bg-subtle, #F1F5F9); border: 1px solid var(--h-border-strong, #CBD5E1); border-right: none; border-radius: var(--h-radius, 6px) 0 0 var(--h-radius, 6px); color: var(--h-text-secondary, #64748B); font-size: 14px; }
.h-pe__input--suffixed { border-radius: 0 var(--h-radius, 6px) var(--h-radius, 6px) 0; }

/* key/value limits */
.h-pe__kv { display: flex; gap: 8px; margin-bottom: 8px; align-items: center; }
.h-pe__input--key { flex: 0 0 42%; font-family: var(--h-font-mono, monospace); font-size: 13px; }
.h-pe__input--val { flex: 1; }
.h-pe__kvx { border: none; background: none; color: var(--h-text-tertiary, #94A3B8); cursor: pointer; font-size: 14px; padding: 6px; border-radius: var(--h-radius, 6px); }
.h-pe__kvx:hover { color: var(--h-danger-text, #B91C1C); background: var(--h-danger-bg, #FEF2F2); }
.h-pe__addrow { font: inherit; font-size: 12.5px; font-weight: 600; color: var(--h-accent-dark, #0369A1); background: var(--h-accent-light, #E0F2FE); border: none; border-radius: var(--h-radius, 6px); padding: 6px 12px; cursor: pointer; }
.h-pe__addrow:focus-visible { outline: none; box-shadow: var(--h-focus-ring); }

/* features tags */
.h-pe__tags { display: flex; flex-wrap: wrap; gap: 6px; padding: 8px; border: 1px solid var(--h-border-strong, #CBD5E1); border-radius: var(--h-radius, 6px); background: var(--h-bg-card, #fff); }
.h-pe__tag { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 500; background: var(--h-bg-subtle, #F1F5F9); color: var(--h-text-secondary, #475569); border-radius: var(--h-radius, 6px); padding: 3px 4px 3px 9px; }
.h-pe__tagx { border: none; background: none; color: var(--h-text-tertiary, #94A3B8); cursor: pointer; font-size: 12px; line-height: 1; padding: 2px 4px; border-radius: 4px; }
.h-pe__tagx:hover { color: var(--h-danger-text, #B91C1C); }
.h-pe__taginput { flex: 1; min-width: 120px; border: none; outline: none; font: inherit; font-size: 13px; background: transparent; color: var(--h-text-primary, #0F172A); padding: 4px; }

/* toggles */
.h-pe__switch { position: relative; display: flex; align-items: center; gap: 12px; cursor: pointer; margin-bottom: 12px; }
.h-pe__switch input { position: absolute; opacity: 0; width: 0; height: 0; }
.h-pe__track { width: 38px; height: 22px; background: var(--h-border-strong, #CBD5E1); border-radius: 999px; position: relative; transition: background 0.15s; flex: none; }
.h-pe__track::after { content: ''; position: absolute; top: 2px; left: 2px; width: 18px; height: 18px; background: #fff; border-radius: 50%; box-shadow: 0 1px 2px rgba(0,0,0,.25); transition: transform 0.15s; }
.h-pe__switch input:checked + .h-pe__track { background: var(--h-accent, #0EA5E9); }
.h-pe__switch input:checked + .h-pe__track::after { transform: translateX(16px); }
.h-pe__switch input:focus-visible + .h-pe__track { box-shadow: var(--h-focus-ring); }
.h-pe__swlbl { font-size: 13.5px; color: var(--h-text-primary, #0F172A); }

/* gateway sync block */
.h-pe__sec { font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: var(--h-text-tertiary, #94A3B8); margin: 22px 0 12px; padding-bottom: 6px; border-bottom: 1px solid var(--h-border, #E2E8F0); }
.h-pe__gwrow { display: flex; align-items: center; gap: 10px; padding: 10px 12px; border: 1px solid var(--h-border, #E2E8F0); border-radius: var(--h-radius, 6px); margin-bottom: 8px; }
.h-pe__gwn { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 500; color: var(--h-text-primary, #0F172A); }
.h-pe__gwn i { width: 9px; height: 9px; border-radius: 2px; flex: none; }
.h-pe__gwbadge { margin-left: auto; }

/* footer buttons */
.h-btn { font: inherit; font-size: 13.5px; font-weight: 500; padding: 9px 15px; border-radius: var(--h-radius, 6px); border: 1px solid transparent; cursor: pointer; display: inline-flex; align-items: center; gap: 7px; }
.h-btn:focus-visible { outline: none; box-shadow: var(--h-focus-ring); }
.h-btn--ghost { background: transparent; color: var(--h-text-secondary, #64748B); }
.h-btn--ghost:hover { background: var(--h-bg-subtle, #F1F5F9); color: var(--h-text-primary, #0F172A); }
.h-btn--primary { background: var(--h-accent, #0EA5E9); color: #fff; }
.h-btn--primary:hover:not(:disabled) { background: var(--h-accent-hover, #0284C7); }
.h-btn--primary:disabled { opacity: 0.6; cursor: not-allowed; }
.h-pe__spin { width: 13px; height: 13px; border: 2px solid rgba(255,255,255,.4); border-top-color: #fff; border-radius: 50%; animation: h-pe-spin 0.7s linear infinite; }
@keyframes h-pe-spin { to { transform: rotate(360deg); } }

/* FormKit genesis → Hermes token pass (scoped) */
.h-pe :deep(.formkit-outer) { margin-bottom: 16px; }
.h-pe :deep(.formkit-label) { display: block; font-size: 13px; font-weight: 500; color: var(--h-text-primary, #0F172A); margin-bottom: 6px; }
.h-pe :deep(.formkit-input) {
  width: 100%; font: inherit; font-size: 14px; background: var(--h-bg-card, #fff);
  border: 1px solid var(--h-border-strong, #CBD5E1); color: var(--h-text-primary, #0F172A);
  border-radius: var(--h-radius, 6px); padding: 9px 12px; box-sizing: border-box;
}
.h-pe :deep(.formkit-input:focus) { outline: none; border-color: var(--h-accent, #0EA5E9); box-shadow: var(--h-focus-ring-soft, 0 0 0 3px rgba(14,165,233,.15)); }
.h-pe :deep(select.formkit-input) { appearance: none; background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='none' stroke='%2364748B' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='M4 6l4 4 4-4'/></svg>"); background-repeat: no-repeat; background-position: right 12px center; padding-right: 34px; }
.h-pe :deep(.formkit-help) { font-size: 12px; color: var(--h-text-tertiary, #94A3B8); margin-top: 6px; }
.h-pe :deep(.formkit-message) { font-size: 12.5px; color: var(--h-danger-text, #B91C1C); margin-top: 6px; }
</style>
